/**
 * Cloud Sync Service with End-to-End Encryption
 *
 * This module provides encrypted cloud storage and synchronization
 * for Excalidraw drawings using Cloudflare R2.
 */

import type { ExcalidrawElement, FileId } from "@excalidraw/element/types";
import type { AppState, BinaryFiles } from "@excalidraw/excalidraw/types";
import type { ImportedDataState } from "@excalidraw/excalidraw/data/types";

// API endpoints (will be same origin in production)
const API_BASE = window.location.origin + "/api/sync";

export interface DrawingData {
  elements: readonly ExcalidrawElement[];
  appState: Partial<AppState>;
  files: BinaryFiles;
}

export interface DrawingMetadata {
  id: string;
  name: string;
  updatedAt: number;
  size: number;
}

export interface CloudSyncConfig {
  syncKey: string;
  autoSync: boolean;
  autoLoad: boolean; // Auto-load on app start
  syncDebounceMs: number; // Debounce delay for auto-sync (default: 3000ms)
  lastSyncTime?: number;
  lastLoadTime?: number; // Last time data was loaded from cloud
}

export interface SyncStatus {
  state: 'idle' | 'syncing' | 'success' | 'error';
  lastSyncTime?: number;
  errorMessage?: string;
}

/**
 * Generate a secure random sync key
 */
export function generateSyncKey(): string {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  const hex = Array.from(array, (byte) =>
    byte.toString(16).padStart(2, "0"),
  ).join("");

  // Format as readable chunks (8 groups of 8 chars)
  const chunks = hex.match(/.{1,8}/g) || [];
  return chunks.join("-");
}

/**
 * Derive encryption key from sync key using PBKDF2
 */
async function deriveCryptoKey(
  syncKey: string,
  salt: Uint8Array,
): Promise<CryptoKey> {
  const encoder = new TextEncoder();
  const keyMaterial = await crypto.subtle.importKey(
    "raw",
    encoder.encode(syncKey),
    "PBKDF2",
    false,
    ["deriveBits", "deriveKey"],
  );

  return crypto.subtle.deriveKey(
    {
      name: "PBKDF2",
      salt: salt.buffer as ArrayBuffer,
      iterations: 100000,
      hash: "SHA-256",
    },
    keyMaterial,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"],
  );
}

/**
 * Encrypt data using AES-GCM
 */
async function encryptData(data: string, syncKey: string): Promise<string> {
  const encoder = new TextEncoder();

  // Generate random salt and IV
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const iv = crypto.getRandomValues(new Uint8Array(12));

  // Derive encryption key
  const key = await deriveCryptoKey(syncKey, salt);

  // Encrypt the data
  const encrypted = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv },
    key,
    encoder.encode(data),
  );

  // Combine salt + iv + encrypted data
  const combined = new Uint8Array(
    salt.length + iv.length + encrypted.byteLength,
  );
  combined.set(salt, 0);
  combined.set(iv, salt.length);
  combined.set(new Uint8Array(encrypted), salt.length + iv.length);

  // Convert to base64 using chunks to avoid stack overflow with large data
  return uint8ArrayToBase64(combined);
}

/**
 * Convert Uint8Array to base64 string without stack overflow
 * Uses chunked approach for large arrays
 */
function uint8ArrayToBase64(bytes: Uint8Array): string {
  // Build binary string character by character to avoid any stack issues
  let binaryString = "";
  const len = bytes.length;

  for (let i = 0; i < len; i++) {
    binaryString += String.fromCharCode(bytes[i]);
  }

  return btoa(binaryString);
}

/**
 * Convert base64 string to Uint8Array without stack overflow
 */
function base64ToUint8Array(base64: string): Uint8Array {
  const binaryString = atob(base64);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes;
}

/**
 * Decrypt data using AES-GCM
 */
async function decryptData(
  encryptedBase64: string,
  syncKey: string,
): Promise<string> {
  // Decode from base64 using chunk-safe method
  const combined = base64ToUint8Array(encryptedBase64);

  // Extract salt, iv, and encrypted data
  const salt = combined.slice(0, 16);
  const iv = combined.slice(16, 28);
  const encrypted = combined.slice(28);

  // Derive decryption key
  const key = await deriveCryptoKey(syncKey, salt);

  // Decrypt the data
  const decrypted = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv },
    key,
    encrypted,
  );

  const decoder = new TextDecoder();
  return decoder.decode(decrypted);
}

/**
 * Cloud Storage Service
 */
export class CloudStorage {
  private config: CloudSyncConfig | null = null;
  private syncStatus: SyncStatus = { state: 'idle' };
  private statusListeners: Array<(status: SyncStatus) => void> = [];
  private autoSyncDebounceTimer: NodeJS.Timeout | null = null;

  /**
   * Initialize cloud sync with a sync key
   */
  async initialize(
    syncKey: string,
    autoSync: boolean = true,
    autoLoad: boolean = true,
  ): Promise<void> {
    this.config = {
      syncKey,
      autoSync,
      autoLoad,
      syncDebounceMs: 900000, // 15 minutes, // Default 15 seconds (reduced frequency for workers)
      lastSyncTime: Date.now(),
    };

    // Save config to localStorage
    this.saveConfig();
  }

  /**
   * Get current sync config
   */
  getConfig(): CloudSyncConfig | null {
    if (!this.config) {
      // Try to load from localStorage
      this.loadConfig();
    }
    return this.config;
  }

  /**
   * Clear sync configuration
   */
  clearConfig(): void {
    this.config = null;
    localStorage.removeItem("excalidraw-cloud-sync-config");
  }

  /**
   * Save config to localStorage
   */
  private saveConfig(): void {
    if (this.config) {
      localStorage.setItem(
        "excalidraw-cloud-sync-config",
        JSON.stringify(this.config),
      );
    }
  }

  /**
   * Load config from localStorage
   */
  private loadConfig(): void {
    const saved = localStorage.getItem("excalidraw-cloud-sync-config");
    if (saved) {
      try {
        this.config = JSON.parse(saved);

        // Migration: Update old configs to new default debounce time (15 minutes)
        // If syncDebounceMs is not set or is the old default (3000ms or 15000ms), update it
        if (this.config) {
          if (
            !this.config.syncDebounceMs ||
            this.config.syncDebounceMs === 3000 ||
            this.config.syncDebounceMs === 15000
          ) {
            console.log(
              `Migrating sync debounce from ${this.config.syncDebounceMs}ms to 900000ms (15 minutes)`,
            );
            this.config.syncDebounceMs = 900000; // 15 minutes
            this.saveConfig(); // Save the migrated config
          }

          // Ensure autoLoad is set (for older configs)
          if (this.config.autoLoad === undefined) {
            this.config.autoLoad = true;
          }
        }
      } catch (error) {
        console.error("Failed to load sync config:", error);
      }
    }
  }

  /**
   * Upload drawing to cloud
   */
  async uploadDrawing(
    drawingId: string,
    drawingName: string,
    data: DrawingData,
  ): Promise<void> {
    console.log("[CloudStorage] uploadDrawing called", { drawingId, drawingName });

    if (!this.config) {
      console.error("[CloudStorage] No config found");
      throw new Error(
        "Cloud sync not initialized. Please set up a sync key first.",
      );
    }

    try {
      console.log("[CloudStorage] Serializing data...");
      // Create a safe serialization of elements (removing potential circular references)
      const safeElements = this.serializeElements(data.elements);

      // Serialize drawing data (safely serialize appState to avoid circular references)
      const jsonData = JSON.stringify({
        elements: safeElements,
        appState: this.serializeAppState(data.appState),
        files: this.serializeFiles(data.files),
      });

      console.log("[CloudStorage] Data serialized, size:", jsonData.length, "bytes");
      console.log("[CloudStorage] Encrypting data...");

      // Encrypt the data
      const encryptedData = await encryptData(jsonData, this.config.syncKey);
      console.log("[CloudStorage] Data encrypted, size:", encryptedData.length, "bytes");

      // Upload to API
      const url = `${API_BASE}/upload`;
      console.log("[CloudStorage] Uploading to:", url);

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          syncKey: this.config.syncKey,
          drawingId,
          encryptedData,
          metadata: {
            name: drawingName,
            updatedAt: Date.now(),
          },
        }),
      });

      console.log("[CloudStorage] Upload response status:", response.status);

      if (!response.ok) {
        const error = await response.json();
        console.error("[CloudStorage] Upload failed:", error);
        throw new Error(error.error || "Upload failed");
      }

      const result = await response.json();
      console.log("[CloudStorage] Upload successful:", result);

      // Update last sync time
      this.config.lastSyncTime = Date.now();
      this.saveConfig();
    } catch (error) {
      console.error("[CloudStorage] Upload error:", error);
      throw error;
    }
  }

  /**
   * Download drawing from cloud
   */
  async downloadDrawing(
    drawingId: string,
  ): Promise<DrawingData & { name: string }> {
    if (!this.config) {
      throw new Error(
        "Cloud sync not initialized. Please set up a sync key first.",
      );
    }

    try {
      const response = await fetch(
        `${API_BASE}/download/${this.config.syncKey}/${drawingId}`,
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Download failed");
      }

      const result = await response.json();

      // Decrypt the data
      const decryptedJson = await decryptData(
        result.encryptedData,
        this.config.syncKey,
      );
      const data = JSON.parse(decryptedJson);

      return {
        elements: data.elements,
        appState: data.appState,
        files: this.deserializeFiles(data.files),
        name: result.metadata.name,
      };
    } catch (error) {
      console.error("Download error:", error);
      throw error;
    }
  }

  /**
   * List all drawings for current sync key
   */
  async listDrawings(): Promise<DrawingMetadata[]> {
    if (!this.config) {
      throw new Error(
        "Cloud sync not initialized. Please set up a sync key first.",
      );
    }

    try {
      const response = await fetch(`${API_BASE}/list/${this.config.syncKey}`);

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "List failed");
      }

      const result = await response.json();
      return result.drawings;
    } catch (error) {
      console.error("List error:", error);
      throw error;
    }
  }

  /**
   * Delete a drawing from cloud
   */
  async deleteDrawing(drawingId: string): Promise<void> {
    if (!this.config) {
      throw new Error(
        "Cloud sync not initialized. Please set up a sync key first.",
      );
    }

    try {
      const response = await fetch(
        `${API_BASE}/delete/${this.config.syncKey}/${drawingId}`,
        { method: "DELETE" },
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Delete failed");
      }
    } catch (error) {
      console.error("Delete error:", error);
      throw error;
    }
  }

  /**
   * Sync current drawing to cloud (auto-save)
   */
  async syncToCloud(
    elements: readonly ExcalidrawElement[],
    appState: AppState,
    files: BinaryFiles,
  ): Promise<void> {
    if (!this.config?.autoSync) {
      return; // Auto-sync is disabled
    }

    const drawingId = this.getCurrentDrawingId();
    const drawingName = appState.name || "Untitled";

    await this.uploadDrawing(drawingId, drawingName, {
      elements,
      appState,
      files,
    });
  }

  /**
   * Sync from cloud to local
   */
  async syncFromCloud(drawingId?: string): Promise<ImportedDataState> {
    const id = drawingId || this.getCurrentDrawingId();
    const data = await this.downloadDrawing(id);

    return {
      elements: data.elements,
      appState: data.appState,
      files: data.files,
    };
  }

  /**
   * Get or generate current drawing ID
   */
  private getCurrentDrawingId(): string {
    let drawingId = localStorage.getItem("excalidraw-current-drawing-id");
    if (!drawingId) {
      drawingId = this.generateDrawingId();
      localStorage.setItem("excalidraw-current-drawing-id", drawingId);
    }
    return drawingId;
  }

  /**
   * Generate a new drawing ID
   */
  private generateDrawingId(): string {
    return `drawing-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Serialize elements for storage (handle potential circular references)
   * Uses a whitelist approach to only copy known safe properties
   */
  private serializeElements(elements: readonly ExcalidrawElement[]): any[] {
    // Known safe element properties (primitives and simple arrays/objects)
    const safeKeys = [
      "id", "type", "x", "y", "width", "height", "angle", "strokeColor",
      "backgroundColor", "fillStyle", "strokeWidth", "strokeStyle",
      "roughness", "opacity", "seed", "version", "versionNonce",
      "index", "isDeleted", "groupIds", "frameId", "link", "locked",
      "customData", "updated",
      // Text element specific
      "text", "fontSize", "fontFamily", "textAlign", "verticalAlign",
      "containerId", "originalText", "autoResize", "lineHeight",
      // Linear element specific (arrows, lines)
      "points", "startBinding", "endBinding", "startArrowhead", "endArrowhead",
      "lastCommittedPoint", "elbowed",
      // Image element specific
      "fileId", "status", "scale", "crop",
      // Freedraw specific
      "simulatePressure", "pressures",
      // Frame specific
      "name",
      // Roundness
      "roundness",
    ];

    return elements.map((element) => {
      const cleanElement: Record<string, any> = {};

      for (const key of safeKeys) {
        if (key in element) {
          const value = (element as any)[key];
          if (value !== undefined) {
            // Handle boundElements specially - only keep id and type
            if (key === "boundElements" && Array.isArray(value)) {
              cleanElement[key] = value.map((bound: any) => ({
                id: bound.id,
                type: bound.type,
              }));
            } else {
              cleanElement[key] = value;
            }
          }
        }
      }

      // Also handle boundElements if it exists
      if ("boundElements" in element && element.boundElements) {
        cleanElement.boundElements = element.boundElements.map((bound: any) => ({
          id: bound.id,
          type: bound.type,
        }));
      }

      return cleanElement;
    });
  }

  /**
   * Serialize AppState for storage (only serializable properties)
   */
  private serializeAppState(appState: Partial<AppState>): Record<string, any> {
    // Only keep simple, JSON-serializable properties
    // Exclude: collaborators (Map), followedBy (Set), element references, React nodes, etc.
    const serializableKeys = [
      "name",
      "viewBackgroundColor",
      "currentItemStrokeColor",
      "currentItemBackgroundColor",
      "currentItemFillStyle",
      "currentItemStrokeWidth",
      "currentItemStrokeStyle",
      "currentItemRoughness",
      "currentItemOpacity",
      "currentItemFontFamily",
      "currentItemFontSize",
      "currentItemTextAlign",
      "currentItemStartArrowhead",
      "currentItemEndArrowhead",
      "currentItemRoundness",
      "gridSize",
      "gridStep",
      "gridModeEnabled",
      "zenModeEnabled",
      "theme",
      "exportBackground",
      "exportEmbedScene",
      "exportWithDarkMode",
      "exportScale",
      "zoom",
      "scrollX",
      "scrollY",
    ];

    const serialized: Record<string, any> = {};
    for (const key of serializableKeys) {
      if (key in appState && appState[key as keyof AppState] !== undefined) {
        const value = appState[key as keyof AppState];
        // Only include primitive values and plain objects
        if (
          value === null ||
          typeof value === "string" ||
          typeof value === "number" ||
          typeof value === "boolean" ||
          (typeof value === "object" && !(value instanceof Map) && !(value instanceof Set))
        ) {
          try {
            // Test if it's serializable
            JSON.stringify(value);
            serialized[key] = value;
          } catch {
            // Skip non-serializable values
          }
        }
      }
    }
    return serialized;
  }

  /**
   * Serialize BinaryFiles for storage
   */
  private serializeFiles(files: BinaryFiles): Record<string, any> {
    const serialized: Record<string, any> = {};
    for (const [id, fileData] of Object.entries(files)) {
      serialized[id] = {
        mimeType: fileData.mimeType,
        id: fileData.id,
        dataURL: fileData.dataURL,
        created: fileData.created,
      };
    }
    return serialized;
  }

  /**
   * Deserialize BinaryFiles from storage
   */
  private deserializeFiles(serialized: Record<string, any>): BinaryFiles {
    const files: BinaryFiles = {};
    for (const [id, data] of Object.entries(serialized)) {
      files[id as FileId] = {
        mimeType: data.mimeType,
        id: data.id,
        dataURL: data.dataURL,
        created: data.created,
        lastRetrieved: Date.now(),
      };
    }
    return files;
  }

  /**
   * Get current sync status
   */
  getSyncStatus(): SyncStatus {
    return { ...this.syncStatus };
  }

  /**
   * Subscribe to sync status changes
   * Returns unsubscribe function
   */
  onSyncStatusChange(callback: (status: SyncStatus) => void): () => void {
    this.statusListeners.push(callback);
    // Return unsubscribe function
    return () => {
      const index = this.statusListeners.indexOf(callback);
      if (index > -1) {
        this.statusListeners.splice(index, 1);
      }
    };
  }

  /**
   * Update sync status and notify listeners
   */
  private updateSyncStatus(status: Partial<SyncStatus>): void {
    this.syncStatus = { ...this.syncStatus, ...status };
    // Notify all listeners
    this.statusListeners.forEach((listener) => {
      try {
        listener(this.getSyncStatus());
      } catch (error) {
        console.error("Error in sync status listener:", error);
      }
    });
  }

  /**
   * Auto-sync to cloud with debouncing
   */
  async autoSyncToCloud(
    elements: readonly ExcalidrawElement[],
    appState: AppState,
    files: BinaryFiles,
    debounceMs?: number,
  ): Promise<void> {
    if (!this.config?.autoSync) {
      return; // Auto-sync is disabled
    }

    const delay = debounceMs ?? this.config.syncDebounceMs ?? 900000; // 15 minutes default

    // Clear existing timer
    if (this.autoSyncDebounceTimer) {
      clearTimeout(this.autoSyncDebounceTimer);
    }

    // Set new timer
    this.autoSyncDebounceTimer = setTimeout(async () => {
      try {
        this.updateSyncStatus({ state: "syncing" });

        const drawingId = this.getCurrentDrawingId();
        const drawingName = appState.name || "Untitled";

        await this.uploadDrawing(drawingId, drawingName, {
          elements,
          appState,
          files,
        });

        this.updateSyncStatus({
          state: "success",
          lastSyncTime: Date.now(),
          errorMessage: undefined,
        });
      } catch (error) {
        console.error("Auto-sync error:", error);
        this.updateSyncStatus({
          state: "error",
          errorMessage:
            error instanceof Error ? error.message : "Unknown sync error",
        });
      }
    }, delay);
  }

  /**
   * Load latest drawing from cloud (for auto-load on app start)
   * Returns the drawing data if successful, null if no data or error
   */
  async autoLoadFromCloud(): Promise<
    (DrawingData & { name: string; id: string }) | null
  > {
    if (!this.config?.autoLoad) {
      return null; // Auto-load is disabled
    }

    try {
      // Get list of drawings
      const drawings = await this.listDrawings();

      if (drawings.length === 0) {
        return null; // No drawings to load
      }

      // Sort by updatedAt to get the most recent
      drawings.sort((a, b) => b.updatedAt - a.updatedAt);
      const latestDrawing = drawings[0];

      // Download the latest drawing
      const data = await this.downloadDrawing(latestDrawing.id);

      // Update last load time
      if (this.config) {
        this.config.lastLoadTime = Date.now();
        this.saveConfig();
      }

      return {
        ...data,
        id: latestDrawing.id,
      };
    } catch (error) {
      console.error("Auto-load error:", error);
      return null; // Fail silently, fall back to local data
    }
  }
}

// Export singleton instance
export const cloudStorage = new CloudStorage();
